import { ItemTagPicker } from '../sharing/ItemTagPicker';
import { useEditorSession } from '../../hooks/useEditorSession';
import {
  useNavigation,
  useNavigationState,
} from '../navigation/NavigationProvider';
import {
  createElement,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  InventoryAuthority,
  CollectionSummary,
  hasCollectionCapability,
} from '@qkindex/contracts';
import { Item } from '../../domain/item';
import { Location } from '../../domain/location';
import { logger } from '../../services/logger';
import { useFeedback } from '../feedback/FeedbackProvider';
import { useItemImageWorkflow } from '../images/useItemImageWorkflow';
import { useItemActions } from './useItemActions';
import { useUnsavedChangesGuard } from '../../hooks/useUnsavedChangesGuard';
import { useLocationEditorActions } from '../locations/useLocationEditorActions';
import { collectionCapabilities } from '../collection/collectionCapabilities';
import { recordKey } from '../collection/multiCollectionIdentity';
import { RapidCaptureDraft } from '../rapidCapture/rapidCaptureTypes';
import type { ItemEditEnqueue } from '../rapidCapture/rapidCaptureTypes';

interface AddItemBaseline {
  keywords: string;
  location: string;
  qty: number;
  hasImage: boolean;
}

const numericQuantity = (qty: number | string) => Number(qty);

export function useItemWorkflows({
  currentUsername,
  items,
  locations,
  lastUsedLocation,
  updateLastUsedLocation,
  onAddLocation,
  onCapacityChanged,
  collectionContext,
  billingRestricted,
  moveLocations = [],
  onMoveItem,
  moveLocationsNotice,
  moveLocationsLoading = false,
  enqueuePhoto,
  enqueueEdit,
  addCollectionId,
  showTagCollectionName = false,
  onManageTags,
}: {
  showTagCollectionName?: boolean;
  onManageTags?: (collectionId: string) => void;
  collectionContext: { userId: string; collections: CollectionSummary[] };
  billingRestricted?: Set<string>;
  moveLocations?: Location[];
  moveLocationsNotice?: string;
  moveLocationsLoading?: boolean;
  onMoveItem?: (
    item: Item,
    destination: Location,
    destinationTagIds?: string[],
  ) => Promise<boolean>;
  currentUsername?: string;
  items: Item[];
  locations: Location[];
  lastUsedLocation: string;
  updateLastUsedLocation: (location: string) => Promise<void>;
  onAddLocation: (collectionId?: string) => void;
  onCapacityChanged?: () => void;
  enqueuePhoto?: (draft: RapidCaptureDraft) => Promise<unknown>;
  enqueueEdit?: ItemEditEnqueue;
  addCollectionId?: string;
}) {
  const { notify } = useFeedback();
  const { updateStates, requestTransition } = useNavigation();
  const [editorRoute, setEditorRoute] = useNavigationState<{
    mode: 'add' | 'details';
    collectionId?: string;
    itemId?: string;
  } | null>('inventory.editor', null);
  const editorMode = editorRoute?.mode;
  const setEditorMode = (mode: 'add' | 'details' | null) =>
    setEditorRoute(
      mode
        ? {
            mode,
            ...(mode === 'details'
              ? {
                  collectionId: selectedItemSnapshot?.collectionId,
                  itemId: selectedItemSnapshot?.id,
                }
              : {}),
          }
        : null,
    );
  const addOpen = editorMode === 'add';
  const detailsOpen = editorMode === 'details';
  const [deleteItemId, setDeleteItemId] = useNavigationState<string | null>(
    'inventory.delete',
    null,
  );
  const [deleting, setDeleting] = useState(false);
  const [saving, setSaving] = useState(false);
  const [addUiPending, setAddUiPending] = useState(false);
  const [tagsPending, setTagsPending] = useState(false);
  const savePending = useRef(false);
  const runSave = async (action: () => Promise<boolean>) => {
    if (savePending.current) return false;
    savePending.current = true;
    setSaving(true);
    try {
      return await action();
    } finally {
      savePending.current = false;
      setSaving(false);
    }
  };
  const deleteOpen = deleteItemId !== null;
  const [lastSelectedItem, setLastSelectedItem] = useState<Item | null>(null);
  const itemSnapshots = useRef(new Map<string, Item>());
  const deletedItems = useRef(new Set<string>());
  const setSelectedItem = useCallback((item: Item | null) => {
    if (item) {
      const key = recordKey(item);
      itemSnapshots.current.set(key, item);
    }
    setLastSelectedItem(item);
  }, []);
  // Reconcile the restoration cache without changing an active editor baseline.
  const observedItems = useRef(new Map<string, Item>());
  items.forEach((item) => {
    const key = recordKey(item);
    if (observedItems.current.get(key) !== item) {
      observedItems.current.set(key, item);
      itemSnapshots.current.set(key, item);
    }
  });
  const detailKey =
    editorRoute?.mode === 'details'
      ? recordKey({
          id: editorRoute.itemId || '',
          collectionId: editorRoute.collectionId,
        })
      : null;
  const liveItem = detailKey
    ? items.find((item) => recordKey(item) === detailKey && !item.deleted)
    : undefined;
  const cachedItem = detailKey
    ? itemSnapshots.current.get(detailKey)
    : undefined;
  const detailPresent =
    detailKey !== null &&
    !deletedItems.current.has(detailKey) &&
    collectionContext.collections.some(
      (collection) =>
        collection.collectionId === editorRoute?.collectionId &&
        collection.lifecycle !== 'deleted',
    );
  const detailBaseline = useEditorSession(
    detailPresent ? detailKey : null,
    detailPresent ? cachedItem || null : null,
  );
  const detailAvailable =
    detailPresent &&
    (!detailBaseline?.localCapture ||
      Boolean(
        liveItem?.localCapture &&
        liveItem.localCapture.authorityKey ===
          detailBaseline.localCapture.authorityKey,
      ));
  const selectedItemSnapshot = detailsOpen
    ? detailAvailable
      ? detailBaseline
      : null
    : lastSelectedItem;
  const currentSelectedItem =
    selectedItemSnapshot &&
    items.find(
      (item) =>
        item.collectionId === selectedItemSnapshot.collectionId &&
        item.id === selectedItemSnapshot.id &&
        item.storagePath === selectedItemSnapshot.storagePath,
    );
  // Image URLs resolve independently of the editor. Keep its original field
  // baseline while sharing the current URL with display, clear and duplicate.
  const selectedItem =
    selectedItemSnapshot && currentSelectedItem
      ? { ...selectedItemSnapshot, image: currentSelectedItem.image }
      : selectedItemSnapshot;
  const localContext =
    liveItem?.localCapture || selectedItemSnapshot?.localCapture;
  const editingLocal = Boolean(selectedItemSnapshot?.localCapture);
  const conflict = useEditorSession(
    detailsOpen ? `${detailKey}:conflict` : null,
    localContext?.conflict || null,
  );
  const [usingSavedDetails, setUsingSavedDetails] = useState(false);
  const holdRef = useRef(localContext?.hold);
  holdRef.current = localContext?.hold;
  const heldOperation = detailsOpen ? localContext?.operationId : undefined;
  useEffect(() => {
    const hold = holdRef.current;
    if (!heldOperation || !hold) return;
    void hold(true).catch(() =>
      notify(
        'Local item activity changed. Reopen the item before saving.',
        'warning',
      ),
    );
    return () => {
      void hold(false).catch(() => undefined);
    };
  }, [heldOperation, detailsOpen, notify]);
  const requestSelectedImage = currentSelectedItem?.requestImage;
  useEffect(() => {
    if (detailsOpen) return requestSelectedImage?.(0);
  }, [detailsOpen, requestSelectedImage]);
  const [duplicateSourceItemId, setDuplicateSourceItemId] = useState<string>();
  const [duplicateDraft, setDuplicateDraft] = useState(false);
  const [addBaseline, setAddBaseline] = useState<AddItemBaseline | null>(null);
  const [addRapidCapture, setAddRapidCapture] = useState(false);
  const [activeLastUsedLocation, setActiveLastUsedLocation] =
    useState(lastUsedLocation);
  useEffect(() => {
    setActiveLastUsedLocation(lastUsedLocation);
  }, [lastUsedLocation]);
  const applyLastUsedLocation = useCallback(
    async (locationToSave: string) => {
      if (!locationToSave) return;
      setActiveLastUsedLocation(locationToSave);
      await updateLastUsedLocation(locationToSave);
    },
    [updateLastUsedLocation],
  );
  const isItemWritable = useCallback(
    (candidateItem: Item): boolean => {
      const coll = candidateItem.collectionId
        ? collectionContext.collections.find(
            (c) => c.collectionId === candidateItem.collectionId,
          )
        : collectionContext.collections[0];
      if (!coll) return false;
      if (billingRestricted?.has(coll.collectionId)) return false;
      return collectionCapabilities(
        'collection-only',
        coll.role,
        coll.lifecycle,
      ).canEditInventory;
    },
    [collectionContext.collections, billingRestricted],
  );
  const [keywords, setKeywords] = useState('');
  const [location, setEditorLocation] = useState('');
  const [qty, setQty] = useState<number | string>(1);
  const [tagIds, setTagIds] = useState<string[]>([]);
  const allLocations = useMemo(
    () => [
      ...new Map(
        [...locations, ...moveLocations].map((entry) => [
          recordKey(entry),
          entry,
        ]),
      ).values(),
    ],
    [locations, moveLocations],
  );
  const findItemLocation = useCallback(
    (item: Item) =>
      allLocations.find(
        (entry) =>
          (item.collectionId
            ? entry.collectionId === item.collectionId
            : true) &&
          (item.locationId
            ? entry.id === item.locationId
            : entry.name === item.location),
      ),
    [allLocations],
  );
  const selectedLocation = selectedItem && findItemLocation(selectedItem);
  const pendingItemLocation = useRef<Item | null>(null);
  const setLocation = useCallback((value: string) => {
    pendingItemLocation.current = null;
    setEditorLocation(value);
  }, []);
  useEffect(() => {
    const pending = pendingItemLocation.current;
    if (!detailsOpen || !pending) return;
    const resolved = findItemLocation(pending);
    if (resolved) {
      const key = recordKey(resolved);
      setLocation(key);
      if (isItemWritable(pending)) {
        void applyLastUsedLocation(key);
      }
    }
  }, [
    detailsOpen,
    findItemLocation,
    setLocation,
    isItemWritable,
    applyLastUsedLocation,
  ]);

  const chosenLocation = allLocations.find(
    (candidate) => recordKey(candidate) === location,
  );
  const targetCollectionId = detailsOpen
    ? selectedItem?.collectionId
    : chosenLocation?.collectionId;
  const targetCollection = collectionContext.collections.find(
    (candidate) => candidate.collectionId === targetCollectionId,
  );
  const targetCanDelete =
    targetCollection &&
    collectionCapabilities(
      'collection-only',
      targetCollection.role,
      targetCollection.lifecycle,
    ).canEditInventory;
  const targetCanEdit =
    targetCanDelete && !billingRestricted?.has(targetCollectionId || '');
  const targetAuthority: InventoryAuthority | undefined =
    targetCollection &&
    targetCanEdit &&
    (detailsOpen || targetCollection.lifecycle === 'active')
      ? {
          userId: collectionContext.userId,
          collectionId: targetCollection.collectionId,
          compatibilityMode: 'collection-only',
        }
      : undefined;
  const targetMembershipVersion = targetCollection?.membershipVersion;
  // AI needs collection authority, but saving still requires a chosen location.
  const addAiCollection = chosenLocation
    ? targetCollection
    : collectionContext.collections.find(
        (collection) => collection.collectionId === addCollectionId,
      );
  const addAiAuthority =
    addAiCollection?.lifecycle === 'active' &&
    !billingRestricted?.has(addAiCollection.collectionId) &&
    collectionCapabilities(
      'collection-only',
      addAiCollection.role,
      addAiCollection.lifecycle,
    ).canEditInventory
      ? {
          userId: collectionContext.userId,
          collectionId: addAiCollection.collectionId,
          compatibilityMode: 'collection-only' as const,
          membershipVersion: addAiCollection.membershipVersion,
        }
      : undefined;
  const scopedLocations = locations.filter(
    (candidate) => candidate.collectionId === targetCollectionId,
  );
  const canMove = Boolean(
    onMoveItem &&
    !editingLocal &&
    targetCanEdit &&
    targetCollection?.lifecycle === 'active' &&
    hasCollectionCapability(targetCollection.role, 'moveItemsOut'),
  );
  const destinationSummary = collectionContext.collections.find(
    (entry) => entry.collectionId === chosenLocation?.collectionId,
  );
  const crossCollectionLocation =
    detailsOpen &&
    chosenLocation &&
    chosenLocation.collectionId !== selectedItem?.collectionId &&
    canMove &&
    destinationSummary?.lifecycle === 'active' &&
    hasCollectionCapability(destinationSummary.role, 'editInventory')
      ? chosenLocation
      : undefined;
  const destinationLocations = (
    detailsOpen && canMove ? allLocations : locations
  ).filter((candidate) => {
    const summary = collectionContext.collections.find(
      (entry) => entry.collectionId === candidate.collectionId,
    );
    return (
      summary &&
      (detailsOpen
        ? candidate.collectionId === selectedItem?.collectionId ||
          (canMove &&
            summary.lifecycle === 'active' &&
            hasCollectionCapability(summary.role, 'editInventory'))
        : summary.lifecycle === 'active' &&
          summary.role !== 'viewer' &&
          (!duplicateSourceItemId ||
            candidate.collectionId === selectedItem?.collectionId))
    );
  });
  const tagCollection = crossCollectionLocation
    ? destinationSummary
    : targetCollection || addAiCollection;
  const canManageTags = Boolean(
    tagCollection &&
    ['owner', 'administrator'].includes(tagCollection.role) &&
    tagCollection.lifecycle === 'active' &&
    !billingRestricted?.has(tagCollection.collectionId),
  );
  const tagScope = tagCollection?.collectionId;
  const previousTagScope = useRef<string | undefined>(undefined);
  useEffect(() => {
    if (
      previousTagScope.current &&
      tagScope &&
      previousTagScope.current !== tagScope
    ) {
      setTagIds([]);
      if (tagIds.length)
        notify(
          'Tags were cleared because the destination collection changed.',
          'info',
        );
    }
    previousTagScope.current = tagScope;
  }, [tagScope, tagIds.length, setTagIds, notify]);
  const tagsChanged =
    JSON.stringify([...tagIds].sort()) !==
    JSON.stringify([...(selectedItem?.tagIds || [])].sort());
  const tagControl =
    addOpen || detailsOpen
      ? createElement(ItemTagPicker, {
          key: `${collectionContext.userId}:${tagScope || ''}`,
          collectionId: tagScope,
          collectionName: tagCollection?.name,
          showCollectionName: showTagCollectionName,
          value: tagIds,
          canManage: canManageTags,
          disabled: saving || addUiPending,
          onChange: setTagIds,
          onManageTags,
          onPendingChange: setTagsPending,
        })
      : undefined;
  const scopedItems = items.filter(
    (candidate) => candidate.collectionId === targetCollectionId,
  );
  if (
    selectedItem &&
    selectedItem.collectionId === targetCollectionId &&
    !scopedItems.some((entry) => entry.id === selectedItem.id)
  )
    scopedItems.push(selectedItem);
  const editorActions = useLocationEditorActions(targetAuthority);

  const imageWorkflow = useItemImageWorkflow({
    selectedItem,
    addOpen,
    detailsOpen,
    lastUsedLocation: activeLastUsedLocation,
    setLocation,
    setQty,
    openAdd: () => {
      if (!addOpen) setTagIds([]);
      setAddBaseline({
        keywords: '',
        location: activeLastUsedLocation,
        qty: 1,
        hasImage: false,
      });
      setEditorMode('add');
    },
  });

  const resetImage = imageWorkflow.reset;

  // History entries carry identity only. Returning to another item must never
  // show the previous item's local fields or pending image against that identity.
  const restoredEditor = useRef<string | null>(null);
  const editorIdentity =
    editorRoute?.mode === 'details'
      ? JSON.stringify([editorRoute.collectionId, editorRoute.itemId])
      : editorRoute?.mode || null;
  useEffect(() => {
    if (restoredEditor.current === editorIdentity) return;
    if (detailsOpen && !selectedItemSnapshot) return;
    restoredEditor.current = editorIdentity;
    if (!detailsOpen || !selectedItemSnapshot) return;
    setKeywords(selectedItemSnapshot.keywords);
    const location = findItemLocation(selectedItemSnapshot);
    setLocation(location ? recordKey(location) : '');
    pendingItemLocation.current = location ? null : selectedItemSnapshot;
    setQty(selectedItemSnapshot.qty);
    setUsingSavedDetails(false);
    setTagIds(selectedItemSnapshot.tagIds || []);
    imageWorkflow.reset();
  }, [
    editorIdentity,
    detailsOpen,
    selectedItemSnapshot,
    findItemLocation,
    setKeywords,
    setLocation,
    setQty,
    setTagIds,
    imageWorkflow,
  ]);

  const resetItemForm = (nextLocation = '') => {
    setDuplicateDraft(false);
    setDuplicateSourceItemId(undefined);
    setKeywords('');
    setLocation(nextLocation);
    setQty(1);
    setTagIds([]);
    imageWorkflow.reset();
  };

  const actions = useItemActions({
    enqueueEdit,
    editingItem: selectedItem,
    authority: targetAuthority,
    deletionAuthority:
      targetCollection && targetCanDelete
        ? {
            userId: collectionContext.userId,
            collectionId: targetCollection.collectionId,
            compatibilityMode: 'collection-only',
          }
        : undefined,
    items: scopedItems,
    locations: scopedLocations,
    keywords: keywords,
    location:
      chosenLocation?.collectionId === targetCollectionId
        ? chosenLocation?.name || ''
        : crossCollectionLocation
          ? selectedItem?.location || ''
          : '',
    qty: qty,
    image: imageWorkflow.image,
    imageBlob: imageWorkflow.croppedImageBlob,
    removeExistingImage: imageWorkflow.removeExistingImage,
    sourceItemId: addOpen ? duplicateSourceItemId : undefined,
    tagIds:
      canManageTags &&
      !crossCollectionLocation &&
      (addOpen ? tagIds.length > 0 : tagsChanged)
        ? tagIds
        : undefined,
    resetForm: (nextLocation) =>
      resetItemForm(nextLocation ? location : nextLocation),
    setLastUsedLocation: () => applyLastUsedLocation(location),
    setSelectedItem,
    setItems: () => undefined,
    closeAdd: () => closeAddAfterSave(),
    closeDetails: () => closeDetailsAfterSave(),
    onCapacityChanged,
  });

  const saveAdd = (addAnother: boolean, analyzeImage = false) =>
    runSave(async () => {
      const photo = imageWorkflow.croppedImageBlob;
      if (tagsPending) return false;
      if (!photo || duplicateDraft || !enqueuePhoto) {
        return addAnother
          ? actions.saveAndAddAnother()
          : actions.saveAndClose();
      }
      if (
        !targetAuthority ||
        !chosenLocation ||
        imageWorkflow.compressionInProgress
      )
        return false;
      const destination = location;
      try {
        await enqueuePhoto({
          image: photo,
          keywords: keywords,
          location: chosenLocation.name,
          locationId: recordKey(chosenLocation),
          qty: numericQuantity(qty),
          analyzeImage,
          ...(canManageTags && tagIds.length ? { tagIds: tagIds } : {}),
        });
      } catch (error) {
        logger.error('Item enqueue failed.', error);
        notify(
          'The item could not be queued. Your draft is still open. Please try again.',
          'error',
        );
        return false;
      }
      resetItemForm(addAnother ? destination : '');
      if (addAnother)
        setAddBaseline({
          keywords: '',
          location: destination,
          qty: 1,
          hasImage: false,
        });
      else {
        setAddBaseline(null);
        closeAddAfterSave();
      }
      // This preference must not turn an accepted queue entry into a failed save.
      void applyLastUsedLocation(destination).catch(() => undefined);
      return true;
    });

  const addDirty =
    !addRapidCapture &&
    Boolean(addBaseline) &&
    (tagIds.length > 0 ||
      keywords !== addBaseline?.keywords ||
      location !== addBaseline?.location ||
      numericQuantity(qty) !== addBaseline?.qty ||
      Boolean(imageWorkflow.hasPendingImage || duplicateSourceItemId) !==
        addBaseline?.hasImage);
  const detailsDirty =
    Boolean(selectedItem) &&
    (tagsChanged ||
      keywords !== selectedItem?.keywords ||
      location !== (selectedLocation ? recordKey(selectedLocation) : '') ||
      numericQuantity(qty) !== selectedItem?.qty ||
      Boolean(imageWorkflow.croppedImageBlob) ||
      imageWorkflow.removeExistingImage);
  const { requestClose: requestAddClose, closeAfterSave: closeAddAfterSave } =
    useUnsavedChangesGuard({
      scopeKey: 'inventory.editor',
      open: addOpen,
      dirty: addDirty,
      pending:
        imageWorkflow.compressionInProgress ||
        saving ||
        addUiPending ||
        tagsPending ||
        deleting,
      onDiscard: () => {
        resetItemForm();
        setAddBaseline(null);
      },
      onClose: () => setEditorMode(null),
    });
  const {
    requestClose: requestDetailsClose,
    closeAfterSave: closeDetailsAfterSave,
    runAfterSave: runDetailsAfterSave,
  } = useUnsavedChangesGuard({
    scopeKey: 'inventory.editor',
    open: detailsOpen,
    dirty: detailsDirty,
    pending:
      imageWorkflow.compressionInProgress ||
      saving ||
      addUiPending ||
      tagsPending ||
      deleting,
    onDiscard: () => {
      resetItemForm();
    },
    onClose: () => setEditorMode(null),
  });

  useEffect(() => {
    if (editorRoute?.mode === 'details' && !detailAvailable) {
      setSelectedItem(null);
      resetImage();
      setKeywords('');
      setLocation('');
      setQty(1);
      setTagIds([]);
      if (detailKey) itemSnapshots.current.delete(detailKey);
      runDetailsAfterSave(() =>
        updateStates(
          { 'inventory.editor': null, 'inventory.delete': null },
          { replace: true },
        ),
      );
    }
  }, [
    editorRoute,
    detailAvailable,
    detailKey,
    setSelectedItem,
    updateStates,
    resetImage,
    runDetailsAfterSave,
    setKeywords,
    setLocation,
    setQty,
    setTagIds,
  ]);
  const prepareAdd = useCallback(
    (location = activeLastUsedLocation) => {
      setDuplicateDraft(false);
      setDuplicateSourceItemId(undefined);
      setSelectedItem(null);
      resetImage();
      setKeywords('');
      setLocation(location);
      setQty(1);
      setTagIds([]);
      setAddBaseline({ keywords: '', location, qty: 1, hasImage: false });
    },
    [
      activeLastUsedLocation,
      resetImage,
      setKeywords,
      setLocation,
      setQty,
      setTagIds,
      setSelectedItem,
    ],
  );
  useEffect(() => {
    if (!addOpen) {
      setAddBaseline(null);
      return;
    }
    // Forward may restore an identity after its private draft has ended.
    if (addBaseline) return;
    prepareAdd();
  }, [addOpen, addBaseline, prepareAdd]);
  const openAdd = () => {
    void requestTransition({ 'inventory.editor': { mode: 'add' } }, () =>
      prepareAdd(),
    );
  };
  const openAddAtLocation = (location: Location, closeKey: string) =>
    requestTransition(
      { [closeKey]: null, 'inventory.editor': { mode: 'add' } },
      () => prepareAdd(recordKey(location)),
      { replace: true },
    );

  const openItem = (item: Item) => {
    const open = () =>
      requestTransition(
        {
          'inventory.editor': {
            mode: 'details',
            collectionId: item.collectionId,
            itemId: item.id,
          },
        },
        () => {
          setDuplicateSourceItemId(undefined);
          imageWorkflow.reset();
          setSelectedItem(item);
          setKeywords(item.keywords);
          const resolved = findItemLocation(item);
          setLocation(resolved ? recordKey(resolved) : '');
          pendingItemLocation.current = resolved ? null : item;
          if (resolved && isItemWritable(item))
            void applyLastUsedLocation(recordKey(resolved));
          setQty(item.qty);
          setUsingSavedDetails(false);
          setTagIds(item.tagIds || []);
        },
      );
    if (
      item.localCapture?.hold &&
      item.localCapture.operationId !== heldOperation
    ) {
      const hold = item.localCapture.hold;
      void hold(true)
        .then(open)
        .then((opened) => {
          if (!opened) return hold(false);
        })
        .catch(() =>
          notify('This item changed. Reopen it before editing.', 'warning'),
        );
    } else void open();
  };

  const requestDelete = (itemId: string) => {
    setDeleteItemId(itemId);
  };

  const cancelDelete = () => {
    setDeleteItemId(null);
  };

  const confirmDelete = async () => {
    if (!deleteItemId || deleting) return;
    setDeleting(true);
    let deleted: boolean | undefined;
    try {
      deleted = await actions.moveToBin(deleteItemId);
    } finally {
      setDeleting(false);
    }
    if (!deleted) return;
    if (selectedItem) deletedItems.current.add(recordKey(selectedItem));
    runDetailsAfterSave(() =>
      updateStates(
        { 'inventory.delete': null, 'inventory.editor': null },
        { replace: true },
      ),
    );
    imageWorkflow.reset();
  };

  const duplicate = async (item: Item) => {
    if (!isItemWritable(item)) return;
    try {
      await requestTransition(
        { 'inventory.editor': { mode: 'add' } },
        () => {
          imageWorkflow.reset();
          setDuplicateDraft(true);
          setDuplicateSourceItemId(
            item.storagePath || item.image ? item.id : undefined,
          );
          setKeywords(`${item.keywords} (Copy)`);
          const location = findItemLocation(item);
          const duplicateLocation = location ? recordKey(location) : '';
          setLocation(duplicateLocation);
          setQty(item.qty);
          setTagIds([]);
          imageWorkflow.setExternalImageUrl(item.image);
          imageWorkflow.setCroppedImageBlob(null);
          setAddBaseline({
            keywords: `${item.keywords} (Copy)`,
            location: duplicateLocation,
            qty: item.qty,
            hasImage: Boolean(item.image || item.storagePath),
          });
        },
        { replace: true },
      );
    } catch (error) {
      logger.error('Error preparing item for duplication:', error);
      notify(
        'Failed to prepare item for duplication. Please try again.',
        'error',
      );
    }
  };

  const resetForCollectionChange = () => {
    setDuplicateDraft(false);
    setDuplicateSourceItemId(undefined);
    setSelectedItem(null);
    setAddBaseline(null);
    setKeywords('');
    setLocation('');
    setQty(1);
    setTagIds([]);
    imageWorkflow.reset();
    runDetailsAfterSave(() =>
      updateStates(
        { 'inventory.editor': null, 'inventory.delete': null },
        { replace: true },
      ),
    );
  };

  return {
    openAdd,
    openAddAtLocation,
    openItem,
    targetCollection,
    setCurrentLocation: setLocation,
    hasUnsavedChanges: addDirty || detailsDirty,
    resetForCollectionChange,
    modalProps: {
      addItem: {
        tagControl,
        tagsPending,
        tagIds: canManageTags && tagIds.length ? tagIds : undefined,
        onPendingChange: setAddUiPending,
        onRapidCaptureChange: setAddRapidCapture,
        analysisAvailable: !duplicateDraft && Boolean(enqueuePhoto),
        aiLocationRequired: !chosenLocation && !addCollectionId,
        pendingImage: imageWorkflow.croppedImageBlob,
        aiAuthority: addAiAuthority,
        open: addOpen,
        onClose: () => {
          void requestAddClose();
        },
        keywords: keywords,
        setKeywords,
        location: location,
        setLocation,
        qty: qty,
        setQty,
        handleSave: (analyzeImage?: boolean) => saveAdd(false, analyzeImage),
        handleSaveAndAddAnother: async (analyzeImage?: boolean) => {
          const succeeded = await saveAdd(true, analyzeImage);
          if (succeeded)
            setAddBaseline({
              keywords: '',
              location: location,
              qty: 1,
              hasImage: false,
            });
          return succeeded;
        },
        locations: destinationLocations,
        setShowAddLocationModal: (open: boolean) =>
          open && onAddLocation(targetCollectionId),
        image: imageWorkflow.image,
        lastUsedLocation: activeLastUsedLocation,
        compressionInProgress: imageWorkflow.compressionInProgress,
        compressionProgress: imageWorkflow.compressionProgress,
        handleImageCapture: imageWorkflow.handleImageCapture,
        handleClearImage: () => {
          setDuplicateSourceItemId(undefined);
          imageWorkflow.clearPendingImage();
        },
        updateLastUsedLocation: applyLastUsedLocation,
        ...editorActions,
      },
      deleteConfirmation: {
        open: deleteOpen,
        onClose: cancelDelete,
        handleConfirmDelete: confirmDelete,
        deleting,
      },
      itemDetails: {
        tagControl,
        tagsPending,
        open: detailsOpen,
        onClose: () => {
          void requestDetailsClose();
        },
        selectedItem,
        image: imageWorkflow.removeExistingImage
          ? null
          : imageWorkflow.image || selectedItem?.image || null,
        keywords: keywords,
        setKeywords,
        location: location,
        setLocation,
        qty: qty,
        setQty,
        handleUpdate: (itemId: string) =>
          runSave(async () => {
            if (tagsPending) return false;
            const item = selectedItem;
            const destination = crossCollectionLocation;
            const saved = await actions.update(itemId, {
              mergeKeywords: usingSavedDetails
                ? false
                : (selectedItemSnapshot?.localCapture?.mergeKeywords ??
                  localContext?.mergeKeywords),
              reviewedRevision: conflict?.revision,
              awaitConfirmation: Boolean(destination),
            });
            if (!saved || !destination || !item || !onMoveItem) return saved;
            return canManageTags && tagIds.length
              ? onMoveItem(item, destination, tagIds)
              : onMoveItem(item, destination);
          }),
        conflict: conflict || undefined,
        onUseSavedDetails: () => {
          if (!conflict) return;
          setKeywords(conflict.keywords);
          setQty(conflict.qty);
          setTagIds(conflict.tagIds || []);
          const location = findItemLocation(conflict);
          setLocation(location ? recordKey(location) : '');
          setUsingSavedDetails(true);
        },
        saveLabel: crossCollectionLocation
          ? 'Save and move'
          : editingLocal
            ? conflict && !usingSavedDetails
              ? 'Save my changes'
              : 'Save'
            : undefined,
        analysisAvailable: !editingLocal,
        canDuplicate: !editingLocal,
        canManageLocations: !editingLocal,
        showMetadata: !editingLocal || localContext?.published,
        locationNotice:
          canMove && !crossCollectionLocation ? moveLocationsNotice : undefined,
        locationsLoading: canMove && moveLocationsLoading,
        handleDeleteFromModal: requestDelete,
        locations: destinationLocations,
        setShowAddLocationModal: (open: boolean) =>
          open && onAddLocation(targetCollectionId),
        handleImageCapture: imageWorkflow.handleImageCapture,
        handleClearImage: imageWorkflow.handleClearImage,
        compressionInProgress: imageWorkflow.compressionInProgress,
        compressionProgress: imageWorkflow.compressionProgress,
        handleDuplicate: duplicate,
        updateLastUsedLocation: applyLastUsedLocation,
        currentUserId: collectionContext.userId,
        currentUsername,
        readOnly: !targetCanEdit,
        canDelete: Boolean(targetCanDelete) && !editingLocal,
        collectionId: targetCollectionId,
        membershipVersion: targetMembershipVersion,
        pendingImage: imageWorkflow.croppedImageBlob,
        aiAuthority:
          targetAuthority && targetMembershipVersion !== undefined
            ? { ...targetAuthority, membershipVersion: targetMembershipVersion }
            : undefined,
        ...editorActions,
      },
    },
  };
}

export type ItemWorkflows = ReturnType<typeof useItemWorkflows>;
